/**
 * Central auth-failure contract — PURE, dependency-free.
 *
 * ONE place answers "what does this failed response mean for the session?".
 * Both Central perimeters consume it:
 *   - store/api.ts      (baseQueryWithReauth — ordinary API traffic)
 *   - components/routing/BootGate.tsx (switch-context at boot / after a switch)
 *
 * Having two perimeters classify independently is what produced the bug this
 * module exists to kill: BootGate answered EVERY 401 with the
 * "Service Unavailable / Retry" overlay, so a session that had merely idled out
 * looked identical to Central being down — and the Retry could never succeed,
 * because retrying with the same dead token returns the same 401 for ever.
 *
 * THE DISTINCTION THIS MODULE EXISTS TO PRESERVE
 * ----------------------------------------------
 * An access token lives 15 minutes. A session lives 30 minutes of idleness and
 * at most 8 hours in total. Those are DIFFERENT events and must never produce
 * the same user experience:
 *
 *   access token expired, session alive -> refresh silently, user sees nothing
 *   session expired or revoked          -> Central login
 *   Central unreachable                 -> outage UI, credential kept, NEVER login
 *
 * Classification is on the machine-readable CODE whenever Central supplies one.
 * A status alone is never enough: 403 means "no Central session" (login) for one
 * code and "membership is dead" (never login) for another, and 401 means
 * "refresh me" for one code and "you are finished" for another.
 *
 * NO IMPORTS. Pure data in, pure decision out, so it is trivially testable and
 * cannot acquire a dependency on React, Redux, fetch or storage.
 */

/** The single recovery action the caller must take. */
export type CentralFailureAction =
  /** Refresh exactly once, then retry the original request exactly once. */
  | 'RETRY_AFTER_REFRESH'
  /** Session is over: clear local auth and send the user to Central login. */
  | 'CENTRAL_LOGIN'
  /** Show the workspace-closed UI. Never login. */
  | 'WORKSPACE_CLOSED'
  /** Show suite navigation / the workspace picker. Never login. */
  | 'WORKSPACE_PICKER'
  /** This member is not assigned this app. Access denied. Never login. */
  | 'APP_ACCESS_DENIED'
  /** Central / the network is down. Keep the credential, show outage, never login. */
  | 'OUTAGE'
  /** Auth-shaped response we cannot interpret. Surface it. NEVER login. */
  | 'SERVICE_FAILURE'
  /** Not an auth failure this contract owns — hand it back untouched. */
  | 'PASS_THROUGH';

export interface CentralFailureInput {
  /**
   * HTTP status, or one of RTK Query's non-HTTP transport statuses
   * ('FETCH_ERROR' | 'TIMEOUT_ERROR' | 'PARSING_ERROR' | 'CUSTOM_ERROR').
   */
  status: number | string;
  /** Parsed response body in whatever shape it arrived. */
  body?: unknown;
  /** True ONLY when the request that failed WAS the refresh/token endpoint. */
  fromRefreshEndpoint?: boolean;
}

export interface CentralFailureDecision {
  action: CentralFailureAction;
  /** The failure code read off the body, or null when none was present. */
  code: string | null;
  /** True only for RETRY_AFTER_REFRESH. */
  recoverable: boolean;
  /**
   * False ONLY for CENTRAL_LOGIN. Everything else must leave the stored
   * credential alone — an outage must never destroy a usable session.
   */
  keepCredential: boolean;
  /** Why, for logs. Never shown to a user verbatim. */
  reason: string;
}

/**
 * Codes that mean the access token aged out but the SESSION is presumed alive.
 * Both spellings are accepted: Central emits TOKEN_EXPIRED and the suite
 * contract also names ACCESS_TOKEN_EXPIRED, and a client that understood only
 * one of them would send half of all routine 15-minute expiries to the login
 * screen.
 */
const REFRESHABLE_CODES: readonly string[] = ['TOKEN_EXPIRED', 'ACCESS_TOKEN_EXPIRED'];

/** Codes that mean the session itself is finished. */
const TERMINAL_CODES: readonly string[] = [
  'SESSION_EXPIRED',
  'SESSION_REVOKED',
  'AUTH_REQUIRED',
  'INVALID_TOKEN',
  'LEGACY_AUTH_DISABLED',
  // A 403 from a capability/workspace guard meaning "there is no Central
  // identity on this request". Only a login helps.
  'CENTRAL_SESSION_REQUIRED',
];

/**
 * 403 codes that mean "your session is fine, this workspace is not yours any
 * more". Logging in again would mint a valid token, authorise the same dead
 * membership, hit the same 403 and loop for ever — so these must NEVER login.
 */
const MEMBERSHIP_CODES: readonly string[] = ['MEMBERSHIP_INACTIVE', 'MEMBERSHIP_REVOKED'];

/** 403 codes meaning the member simply has not been assigned this app. */
const APP_DENIED_CODES: readonly string[] = ['APP_ACCESS_DENIED', 'MEMBER_NOT_ASSIGNED'];

/** OAuth2 token-endpoint errors (RFC 6749 §5.2) proving the refresh token is dead. */
const OAUTH_TERMINAL_ERRORS: readonly string[] = ['invalid_grant', 'invalid_token'];

/** Transport statuses meaning the round trip never completed. */
const OUTAGE_TRANSPORT_STATUSES: readonly string[] = ['FETCH_ERROR', 'TIMEOUT_ERROR'];

/** Statuses on which an auth failure code is meaningful. */
function isAuthRelevantStatus(status: number): boolean {
  return status === 400 || status === 401 || status === 403 || status === 409;
}

/** SCREAMING_SNAKE_CASE — the shape of a Central auth-vocabulary code. */
export function isScreamingSnakeCode(code: string): boolean {
  return /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/.test(code);
}

/**
 * Pull the failure code out of an arbitrary body, in priority order:
 *   { code }  { error: 'x' }  { error: { code } }  { data: { code } }
 */
export function extractFailureCode(body: unknown): string | null {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (trimmed !== '' && /^[A-Za-z][A-Za-z0-9_]*$/.test(trimmed)) return trimmed;
    return null;
  }
  if (body === null || typeof body !== 'object') return null;

  const record = body as Record<string, unknown>;

  if (typeof record.code === 'string' && record.code.trim() !== '') return record.code.trim();
  if (typeof record.error === 'string' && record.error.trim() !== '') return record.error.trim();
  if (record.error !== null && typeof record.error === 'object') {
    const nested = record.error as Record<string, unknown>;
    if (typeof nested.code === 'string' && nested.code.trim() !== '') return nested.code.trim();
  }
  if (record.data !== null && typeof record.data === 'object') {
    const nested = record.data as Record<string, unknown>;
    if (typeof nested.code === 'string' && nested.code.trim() !== '') return nested.code.trim();
  }
  return null;
}

/** Read `otherWorkspaces` off a failure body. Always an array; never throws. */
export function extractOtherWorkspaces(body: unknown): unknown[] {
  if (body === null || typeof body !== 'object') return [];
  const record = body as Record<string, unknown>;
  if (Array.isArray(record.otherWorkspaces)) return record.otherWorkspaces;
  if (record.data !== null && typeof record.data === 'object') {
    const nested = record.data as Record<string, unknown>;
    if (Array.isArray(nested.otherWorkspaces)) return nested.otherWorkspaces;
  }
  return [];
}

function decide(
  action: CentralFailureAction,
  code: string | null,
  reason: string,
): CentralFailureDecision {
  return {
    action,
    code,
    recoverable: action === 'RETRY_AFTER_REFRESH',
    keepCredential: action !== 'CENTRAL_LOGIN',
    reason,
  };
}

/**
 * Classify one failed response into exactly one recovery action.
 * Pure: same input always yields the same output, no side effects.
 */
export function classifyCentralFailure(input: CentralFailureInput): CentralFailureDecision {
  const { status, body } = input;
  const fromRefresh = input.fromRefreshEndpoint === true;
  const code = extractFailureCode(body);

  // 1. Transport failure: offline, DNS, reset, timeout. Always an outage —
  //    including when it happens DURING the refresh. Keep the credential.
  if (typeof status === 'string') {
    if (OUTAGE_TRANSPORT_STATUSES.indexOf(status) !== -1) {
      return decide('OUTAGE', code, `transport failure: ${status}`);
    }
    // The round trip happened but the payload is unusable. Not a credential
    // problem, so never a login.
    return decide('SERVICE_FAILURE', code, `unusable transport status: ${status}`);
  }

  // Some stacks report "no HTTP response at all" as status 0.
  if (status === 0) return decide('OUTAGE', code, 'no HTTP response (status 0)');

  // 2. Server-side outage. 502/503/504 included. Never login, never loop.
  if (status >= 500) return decide('OUTAGE', code, `server error ${status}`);
  if (status === 408) return decide('OUTAGE', code, 'server reported request timeout (408)');

  // 3. WORKSPACE_CLOSED — Central spells it at 400 AND 409. Never login.
  if (code === 'WORKSPACE_CLOSED' && (status === 400 || status === 409)) {
    return decide('WORKSPACE_CLOSED', code, `workspace closed (${status})`);
  }

  // 4. 403 split on the CODE, never the status.
  if (status === 403 && code !== null) {
    if (MEMBERSHIP_CODES.indexOf(code) !== -1) {
      return decide('WORKSPACE_PICKER', code, `membership not usable: ${code}`);
    }
    if (APP_DENIED_CODES.indexOf(code) !== -1) {
      return decide('APP_ACCESS_DENIED', code, `app not assigned to this member: ${code}`);
    }
  }

  // 5. OAuth2 rejection of the refresh token itself — terminal, but ONLY when
  //    we really were talking to the refresh endpoint.
  if (
    fromRefresh &&
    code !== null &&
    OAUTH_TERMINAL_ERRORS.indexOf(code) !== -1 &&
    (status === 400 || status === 401)
  ) {
    return decide('CENTRAL_LOGIN', code, `refresh token rejected by Central: ${code}`);
  }

  // 6. Central vocabulary on an auth-relevant status.
  if (code !== null && isAuthRelevantStatus(status)) {
    if (REFRESHABLE_CODES.indexOf(code) !== -1) {
      // The refresh endpoint saying "your token is expired" cannot be cured by
      // refreshing again. That IS the session ending.
      if (fromRefresh) {
        return decide('CENTRAL_LOGIN', code, `${code} from the refresh endpoint`);
      }
      return decide('RETRY_AFTER_REFRESH', code, 'access token expired — refreshable');
    }

    if (TERMINAL_CODES.indexOf(code) !== -1) {
      return decide('CENTRAL_LOGIN', code, `terminal auth code: ${code}`);
    }
  }

  // 7. A 401 we cannot interpret: no code, a lower_snake service code, or an
  //    unrecognised SCREAMING_SNAKE code. A SERVICE failure, never a session
  //    verdict — so never a login.
  if (status === 401) {
    if (code === null) return decide('SERVICE_FAILURE', null, '401 with no failure code');
    if (!isScreamingSnakeCode(code)) {
      return decide('SERVICE_FAILURE', code, `401 with non-contract code shape: ${code}`);
    }
    return decide('SERVICE_FAILURE', code, `401 with unrecognised code: ${code}`);
  }

  // 8. Ordinary 400 validation, a plain 403 permission denial, 404, 409, 429…
  //    Not ours.
  return decide('PASS_THROUGH', code, `not an auth failure (status ${status})`);
}

export default classifyCentralFailure;
